import { useContext, useEffect } from 'react';

import { FormFieldState, FormType } from '@rippling/utils';
import { getMappedTimeZone } from '@rippling/utils/demotime/helpers';

import { SavaContext } from '../../providers/SavaProvider';
import { SchedulerFlowType } from '../../types';

interface SchedulerParams {
  Company_Name: string,
  debug: boolean,
  Full_Name: string,
  Have_you_run_payroll_this_year: string,
  Headquarters: string,
  How_did_you_hear_about_us: string,
  mappedClientTimeZone: string,
  Number_of_employees: string,
  Phone: string,
  work_email_address: string,
}

interface SchedulerEvent {
  assigneeEmail?: string;
  assigneeId?: string;
  assigneeUserId?: string;
  meetingDuration?: number;
  meetingEndDateTimeUTC?: string;
  meetingId?: string;
  meetingLocalTimezone?: string;
  meetingStartDateTimeUTC?: string;
  routingId?: string;
  routingName?: string;
}

interface SchedulerFlowPayload extends FormFieldState {
  ActivityDate: string;
  ActivityDateTime: string;
  ActivityDuration: number;
  ActivityTimeZone: string;
  AssigneeEmail: string;
  AssigneeId: string;
  clientTimeZone: string;
  DemotimeMeetingId: string;
  didMeetingAvailabiltyLoad: boolean;
  EventEndDateTime: string;
  EventOwnerId: string;
  EventStartDateTime: string;
  Queue_Name_CP__c: string;
  Router_Name_CP__c: string;
  schedulerEvents: string;
}

interface SchedulerFlowDefaultAttributes {
  demotimeClosed?: boolean;
  demotimeEligible?: boolean;
  demotimeLoaded?: boolean;
  didMeetingAvailabiltyLoad?: boolean;
}

export const getSchedulerFlowDefaultAttributes = (props: SchedulerFlowDefaultAttributes = {}) => {
  return {
    demotimeClosed: false,
    demotimeEligible: false,
    demotimeLoaded: false,
    didMeetingAvailabiltyLoad: false,
    ...props,
  };
};

const mapFormValuesToDemotime = ({
  Company = '',
  Country = '',
  Country_Dialing_Code__c = '',
  debugMode = false,
  Email = '',
  FirstName = '',
  LastName = '',
  NumberOfEmployees = '',
  Phone = '',
  user_lead_source = '',
  who_referred_you = '',
}): SchedulerParams => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const mappedClientTimeZone = getMappedTimeZone(clientTimeZone);

  return {
    Company_Name: Company,
    debug: debugMode,
    Full_Name: [FirstName, LastName].filter(Boolean).join(' '),
    Have_you_run_payroll_this_year: Number(NumberOfEmployees) > 0 ? 'Yes' : 'No',
    Headquarters: Country,
    How_did_you_hear_about_us: [user_lead_source, who_referred_you].filter(Boolean).join(', '),
    mappedClientTimeZone,
    Number_of_employees: NumberOfEmployees,
    Phone: [Country_Dialing_Code__c, Phone].filter(Boolean).join(' '),
    work_email_address: Email,
  };
};

const createDemotimeQueryStringFromObject = (obj: SchedulerParams) => {
  const params = new URLSearchParams(Object.entries(obj).reduce((acc, [key, value]) => {
    acc[key as string] = value.toString();

    return acc;
  }, {} as { [key: string]: string }));

  const queryString = params.toString();

  return queryString.replaceAll('+', '%20');
};

const Scheduler = ({
  failRedirectUrl,
  formData,
  formType,
  isSchedulerEligible,
  schedulerFlowType,
  successRedirectUrl,
}: {
  failRedirectUrl: string,
  formData: FormFieldState,
  formType: FormType,
  isSchedulerEligible: boolean,
  schedulerFlowType: SchedulerFlowType | undefined,
  successRedirectUrl: string,
}) => {
  const contextValue = useContext(SavaContext);
  const {
    debugMode = false, schedulerBaseUrl = '', submitForm,
  } = contextValue || {};

  useEffect(() => {
    if (!isSchedulerEligible) {
      return;
    }

    document.body.style.overflow = isSchedulerEligible ? 'hidden' : 'auto';

    let schedulerFlowPayload: SchedulerFlowPayload = {
      ActivityDate: '',
      ActivityDateTime: '',
      ActivityDuration: 0,
      ActivityTimeZone: '',
      AssigneeEmail: '',
      AssigneeId: '',
      clientTimeZone: '',
      DemotimeMeetingId: '',
      EventEndDateTime: '',
      EventOwnerId: '',
      EventStartDateTime: '',
      Queue_Name_CP__c: '',
      Router_Name_CP__c: '',
      schedulerEvents: '',
      ...getSchedulerFlowDefaultAttributes({ demotimeEligible: true }),
    };

    let didBookingSucceed = false;
    let didCloseScheduler = false;
    let schedulerEvents: SchedulerEvent = {};
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const schedulerMessageHandler = (event: MessageEvent) => {
      if (event.origin !== schedulerBaseUrl) {
        return;
      }

      schedulerEvents = {
        ...schedulerEvents,
        ...event.data.payload as Partial<SchedulerEvent>,
      };

      if (event.data.eventName === 'availability-loaded') {
        schedulerFlowPayload = {
          ...schedulerFlowPayload,
          demotimeLoaded: true,
          didMeetingAvailabiltyLoad: true,
        };
      }

      if (event.data.eventName === 'booking-confirmed') {
        didBookingSucceed = true;

        const {
          assigneeEmail = '',
          assigneeId = '',
          assigneeUserId = '',
          meetingDuration = 0,
          meetingEndDateTimeUTC = '',
          meetingId = '',
          meetingLocalTimezone = '',
          meetingStartDateTimeUTC = '',
          routingId = '',
          routingName = '',
        } = schedulerEvents;

        schedulerFlowPayload = {
          ...schedulerFlowPayload,
          ActivityDate: meetingStartDateTimeUTC,
          ActivityDateTime: meetingStartDateTimeUTC,
          ActivityDuration: meetingDuration,
          ActivityTimeZone: meetingLocalTimezone,
          AssigneeEmail: assigneeEmail,
          AssigneeId: assigneeId,
          clientTimeZone,
          DemotimeMeetingId: meetingId,
          EventEndDateTime: meetingEndDateTimeUTC,
          EventOwnerId: assigneeUserId,
          EventStartDateTime: meetingStartDateTimeUTC,
          Queue_Name_CP__c: routingId,
          Router_Name_CP__c: routingName,
          schedulerEvents: JSON.stringify(schedulerEvents),
        };

        handleBookingConfirmed();
      }

      if (['idle', 'close'].includes(event.data.eventName)) {
        didCloseScheduler = true;
        schedulerFlowPayload = {
          ...schedulerFlowPayload,
          demotimeClosed: true,
        };
        handleIdleOrClose();
      }
    };

    const handleBookingConfirmed = async () => {
      window.removeEventListener('message', schedulerMessageHandler);

      if (didBookingSucceed) {
        try {
          const schedulerFormPayload = {
            ...formData,
            ...schedulerFlowPayload,
          };

          await handleSubmitScheduler(schedulerFormPayload, successRedirectUrl);
        } catch (error) {
          console.error('[Demotime] Form submission error:', error);
        }
      }
    };

    const handleIdleOrClose = async () => {
      window.removeEventListener('message', schedulerMessageHandler);

      if (didCloseScheduler) {
        try {
          const schedulerFormPayload = {
            ...formData,
            ...schedulerFlowPayload,
          };

          await handleSubmitScheduler(schedulerFormPayload, failRedirectUrl);
        } catch (error) {
          console.error('[Demotime] Form submission error:', error);
        }
      }
    };

    window.addEventListener('message', schedulerMessageHandler);

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSchedulerEligible]);

  const handleSubmitScheduler = async (payload: FormFieldState, redirectUrl: string) => {
    if (!submitForm || !payload) {
      return;
    }

    try {
      await submitForm({
        formData: payload,
        formType: formType,
        thankYouUrl: redirectUrl,
      });
    } catch (error) {
      console.error('[Demotime] Form submission error:', error);
    }
  };

  return (
    <>
      {isSchedulerEligible && (
        <div
          className="fixed inset-0 bg-black-50 flex items-center justify-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
          }}
        >
          <div
            className="max-w-800 w-full mx-4 overflow-hidden"
            style={{
              maxHeight: '95vh',
              maxWidth: '800px',
              minHeight: '85vh',
            }}
          >
            <iframe
              src={(() => {
                if (SchedulerFlowType.ItFreeTrial === schedulerFlowType) {
                  formData.Country = 'IT_Free_Trial';
                }

                const params = mapFormValuesToDemotime({
                  ...formData,
                  debugMode,
                });
                const schedulerParams = createDemotimeQueryStringFromObject(params);

                const isSandboxUrl = schedulerBaseUrl?.includes('uat');
                const url = `${schedulerBaseUrl}/${isSandboxUrl ? 'meetings/' : ''}embedded-queue?${schedulerParams}`;

                return url;
              })()}
              style={{
                border: 'none',
                height: '100%',
                left: 0,
                position: 'absolute',
                top: 0,
                width: '100%',
              }}
              title="Embedded Queue"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Scheduler;
